import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { environment } from '@/env/env.prod';
import { Smartphone, Monitor, Tablet, Download, CheckCircle, LucideProps } from 'lucide-react';
import { ForwardRefExoticComponent, RefAttributes, useEffect, useState } from 'react';
import downloadImageBackground from '../Assets/download-screen-web.jpg'
export default function DownloadPage() {
  const [ scrollY, setScrollY ] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const platforms = [
    {
      icon: Smartphone,
      name: 'iOS',
      description: 'Download for iPhone and iPad',
      version: 'Version 2.1.0',
      requirements: 'iOS 14.0 or later',
      link: environment.IOS_LINK
    },
    {
      icon: Smartphone,
      name: 'Android',
      description: 'Download for Android devices',
      version: 'Version 2.1.0',
      requirements: 'Android 8.0 or later',
      link: environment.ANDROID_LINK
    },
    {
      icon: Monitor,
      name: 'Windows',
      description: 'Download for Windows PC',
      version: 'Version 2.1.0',
      requirements: 'Windows 10 or later',
    },
    {
      icon: Monitor,
      name: 'macOS',
      description: 'Download for Mac',
      version: 'Version 2.1.0',
      requirements: 'macOS 11.0 or later',
    },
  ];

  const features = [
    'Offline access to your materials',
    'Real-time sync across all devices',
    'Push notifications for updates',
    'Optimized performance',
    'Secure data encryption',
    'Regular updates and improvements',
  ];

  function handleDownload(platform: { icon: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>; name: string; description: string; version: string; requirements: string; link: string; } | { icon: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>; name: string; description: string; version: string; requirements: string; link?: undefined; }): void {
    if (platform.link) {
      window.open(platform.link, "_blank", "noopener,noreferrer");
    }
  }

  function autoDownloadForPlatform() {
    const ua = navigator.userAgent.toLowerCase();
    console.log("User Agent Device :: ", ua);
    switch (true) {
      case ua.includes("android"):
        window.location.href =
          environment.ANDROID_LINK;
        break;

      case /iphone|ipad|ipod/.test(ua):
        window.location.href =
          environment.IOS_LINK;
        break;

      //case ua.includes("win"):
      //  window.location.href =
      //    "https://cdn.yoursite.com/app/windows-installer.exe";
      //  break;

      //case ua.includes("mac"):
      //  window.location.href =
      //    "https://cdn.yoursite.com/app/mac-installer.dmg";
      //  break;

      //case ua.includes("linux"):
      //  window.location.href =
      //    "https://cdn.yoursite.com/app/linux.AppImage";
      //  break;

      default:
        alert("Your platform is not supported yet.");
    }
  }


  return (
    <div className="flex flex-col ">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-12">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            backgroundImage: 'radial-gradient(circle, #004CFF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="container relative z-10">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
            <div className="flex flex-col  justify-center space-y-5">
              <h1 className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-5xl font-extrabold tracking-tight text-brand-black md:text-6xl">
                Download UNILinkd
              </h1>
              <p className="text-xl font-medium text-brand-black md:text-2xl">
                Get started with UNILinkd on your favorite device. Available for mobile, tablet, and desktop.
              </p>
              <div className="flex flex-col  gap-4 sm:flex-row">
                <Button
                  size="lg"
                  className="group rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] text-lg font-bold text-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
                  onClick={() => autoDownloadForPlatform()}
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download Now
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                <img
                  src={downloadImageBackground}
                  alt="UNILinkd Mobile App"
                  className="relative max-h-[500px] w-auto rounded-3xl shadow-2xl transition-transform hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section
        className="relative bg-brand-white py-12 mb-12"
        style={{
          transform: `translateY(${scrollY * 0.1}px)`,
        }}
      >
        <div className="container">
          <div className="mb-8 text-center">
            <h2 className="mb-3 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black">
              Available on All Your Devices
            </h2>
            <p className="text-xl font-medium text-brand-black">
              Choose your platform and start learning today
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-20 md:mb-0">
            {platforms.map((platform, index) => (
              <Card key={index} className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                <CardHeader>
                  <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-lg">
                    <platform.icon className="h-8 w-8 text-brand-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-brand-black">{platform.name}</CardTitle>
                  <CardDescription className="text-base font-medium text-brand-black">{platform.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1 text-sm font-medium text-brand-black">
                    <p>{platform.version}</p>
                    <p>{platform.requirements}</p>
                  </div>
                  {platform.icon != Monitor ? (
                    <Button className="w-full rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] font-bold text-brand-white shadow-lg transition-all hover:scale-105 hover:shadow-xl"
                      onClick={() => handleDownload(platform)}>
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </Button>
                  ) : (
                    <Button disabled className="w-full rounded-xl">
                      Coming Soon
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-12">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
            backgroundImage: 'linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF), linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF)',
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px',
          }}
        />
        <div className="container relative z-10">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 text-center">
              <h2 className="mb-3 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black">
                What You Get
              </h2>
              <p className="text-xl font-medium text-brand-black">
                Premium features included with every download
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="mt-0.5 h-6 w-6 flex-shrink-0 text-brand-blue" />
                  <span className="font-medium text-brand-black">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* System Requirements */}
      <section
        className="relative bg-brand-white py-12"
      //style={{
      //  transform: `translateY(${scrollY * 0.15}px)`,
      //}}
      >
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-6 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black">System Requirements</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="rounded-3xl bg-brand-white shadow-xl">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-brand-black">Mobile Devices</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm font-medium text-brand-black">
                  <p>• iOS 14.0 or later</p>
                  <p>• Android 8.0 or later</p>
                  <p>• 100 MB free storage</p>
                  <p>• Internet connection required</p>
                </CardContent>
              </Card>
              <Card className="rounded-3xl bg-brand-white shadow-xl">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-brand-black">Desktop Computers</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm font-medium text-brand-black">
                  <p>• Windows 10 or macOS 11.0+</p>
                  <p>• 4 GB RAM minimum</p>
                  <p>• 200 MB free storage</p>
                  <p>• Internet connection required</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
